import {
  Affix,
  Col,
  Divider,
  Drawer,
  Flex,
  LegacyCard,
  LegacyForm,
  Progress,
  Row,
  Space,
  Tooltip,
  Typography,
  usePaypingContext,
} from "@payping/web-components";
import { commaMaskedInputHelper } from "@payping/utils/string";

import { CheckCircleFilled, CloseCircleFilled, CuToman, InfoCircleOutlined } from "@payping/icons";
import { useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import useTransferLabel from "@/utils/useTransferLabel";
import { AnchorButtons } from "@/pages/Settlement/Create/Anchor/buttons";
import {
  CreateStepEnum,
  OverviewType,
  SettlementConfiguration,
  SettlementCreateFormType,
} from "@/pages/Settlement/Create";
import React from "react";
import type { FormInstance } from "@payping/web-components/legacyComponents/Form/types";
import { useParams } from "react-router-dom";
import { TransferTypeEnum } from "@payping/api";
import { SettlementRowType } from "../secondStep";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

interface Props {
  loadingStepOne: boolean;
  loadingStepTwo: boolean;
  prev: () => void;
  formStepOne: FormInstance<SettlementConfiguration>;
  formStepTwo: FormInstance<SettlementRowType>;
  formStepThree: FormInstance<SettlementCreateFormType>;
  loadingDelete: boolean;
  requestTitle?: string;
  requestDesc?: string;
  transferType: TransferTypeEnum;
  overviewData: OverviewType;
  setOpenModalCancel: React.Dispatch<React.SetStateAction<boolean>>;
  amountStepOne: number;
  amountStepThree: number;
}

const DataCardStep = (prop: Props) => {
  const { step } = useParams<{ step: string }>() as { step: string };
  const stepNumber = Number(step) as CreateStepEnum;
  const {
    loadingStepOne,
    loadingStepTwo,
    prev,
    loadingDelete,
    overviewData,
    setOpenModalCancel,
    formStepOne,
    formStepTwo,
    formStepThree,
    requestTitle,
    requestDesc,
    transferType,
    amountStepOne,
    amountStepThree,
  } = prop;
  const { t } = useAppTranslation();
  const { overview } = useAppSelector((state) => state.cashOuts);
  const { createdRequest } = useAppSelector((state) => state.cashOuts);
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;
  const { getLabelTransfer } = useTransferLabel();

  const getSettlementListStepTwo = LegacyForm.useWatch("settlement", formStepTwo);
  const getSettlementListStepThree = createdRequest?.settlements?.length;

  const errorCount = getSettlementListStepTwo?.reduce((acc: any, item: any) => acc + (item.error ? 1 : 0), 0);
  const getAmount =
    getSettlementListStepTwo?.reduce(
      (acc: any, item: any) => acc + Number(commaMaskedInputHelper.unMask(item.amount)),
      0,
    ) ||
    amountStepOne ||
    amountStepThree ||
    0;

  return lg ? (
    <Affix offsetTop={20}>
      <LegacyCard.Ant
        title={
          <LegacyForm.Item noStyle>
            <div className={"tw:flex tw:justify-between tw:pt-4"}>
              <Space orientation={"vertical"}>
                <Typography.Text type={"secondary"}>{t("settlement.create.settlementAmount")}</Typography.Text>
                <Flex align="start">
                  <Typography.Title
                    level={5}
                  >{`${formatLocalizedNumber({ value: Number(getAmount), options: { maximumFractionDigits: 2 } })} از ${formatLocalizedNumber({ value: overview?.withdrawableBalance ?? 0, options: { maximumFractionDigits: 2 } })}`}</Typography.Title>
                  <CuToman width={24} height={24} />
                </Flex>
              </Space>
              <Progress
                strokeLinecap="square"
                type="circle"
                percent={
                  overview?.withdrawableBalance && getAmount
                    ? Math.trunc((getAmount / overview.withdrawableBalance) * 100)
                    : 0
                }
                size={20}
              />
            </div>
          </LegacyForm.Item>
        }
      >
        <Space orientation={"vertical"} className="tw:w-full" size={"middle"}>
          <LegacyForm.Item noStyle>
            <div className={"tw:flex tw:justify-between"}>
              <div className={"tw:me-2! tw:shrink-0"}>
                <Typography.Text type={"secondary"}>{t("settlement.create.nameList")}</Typography.Text>
              </div>

              <Typography.Text ellipsis>
                <Tooltip title={requestTitle}>{requestTitle}</Tooltip>
              </Typography.Text>
            </div>
          </LegacyForm.Item>
          <LegacyForm.Item noStyle>
            <div className={"tw:flex tw:justify-between"}>
              <div className={"tw:me-2! tw:shrink-0"}>
                <Typography.Text type={"secondary"}>{t("description")}</Typography.Text>
              </div>

              <Typography.Text ellipsis>
                <Tooltip title={requestDesc}>{requestDesc}</Tooltip>
              </Typography.Text>
            </div>
          </LegacyForm.Item>
          <LegacyForm.Item noStyle>
            <Row gutter={16}>
              <Col>
                <Typography.Text type={"secondary"}>{t("settlement.create.settlementType")}</Typography.Text>
              </Col>
              <Col flex={1} className={"tw:flex tw:justify-end"}>
                <Typography.Text>{getLabelTransfer(transferType)}</Typography.Text>
              </Col>
            </Row>
          </LegacyForm.Item>
          {stepNumber === CreateStepEnum.IBANList ? (
            <>
              <Divider className={"tw:m-0!"} />
              <LegacyForm.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>{t("settlement.create.settlementNumbers")}</Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw:flex tw:justify-end"}>
                    <Typography.Text>
                      {getSettlementListStepTwo?.length
                        ? t("settlement.create.showLength", { length: getSettlementListStepTwo.length })
                        : t("settlement.create.itemNotAdded")}
                    </Typography.Text>
                  </Col>
                </Row>
              </LegacyForm.Item>
              <LegacyForm.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>{t("settlement.create.listStatus")}</Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw:flex tw:justify-end"}>
                    {getSettlementListStepTwo?.length ? (
                      <Typography.Text>
                        {errorCount ? (
                          <Space>
                            <CloseCircleFilled width={14} className={"color--danger tw:align-center tw:flex"} />
                            {t("settlement.create.showError", { error: errorCount })}
                          </Space>
                        ) : (
                          <Space>
                            <CheckCircleFilled width={14} className={"color--success tw:align-center tw:flex"} />
                            {t("settlement.create.noneError")}
                          </Space>
                        )}
                      </Typography.Text>
                    ) : (
                      "-"
                    )}
                  </Col>
                </Row>
              </LegacyForm.Item>
            </>
          ) : null}

          {stepNumber === CreateStepEnum.Verification ? (
            <>
              <Divider className={"tw:m-0!"} />
              <LegacyForm.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>{t("settlement.create.settlementNumbers")}</Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw:flex tw:justify-end"}>
                    <Typography.Text>
                      {getSettlementListStepThree
                        ? t("settlement.create.showLength", { length: getSettlementListStepThree })
                        : t("settlement.create.itemNotAdded")}
                    </Typography.Text>
                  </Col>
                </Row>
              </LegacyForm.Item>

              <LegacyForm.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>{t("settlement.totalCashout")}</Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw:flex tw:justify-end"}>
                    <Typography.Text>{overviewData?.totalAmount}</Typography.Text>
                  </Col>
                </Row>
              </LegacyForm.Item>
              <LegacyForm.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>{t("settlement.totalWage")}</Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw:flex tw:justify-end"}>
                    <Typography.Text>{overviewData?.totalWage}</Typography.Text>
                  </Col>
                </Row>
              </LegacyForm.Item>
              <LegacyForm.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>
                      <Space>
                        {t("settlement.total")}
                        <Tooltip className="tw:align-middle" placement="top" title={t("settlement.totalDescription")}>
                          <InfoCircleOutlined width={"14px"} />
                        </Tooltip>
                      </Space>
                    </Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw:flex tw:justify-end"}>
                    <Typography.Text>{overviewData?.total}</Typography.Text>
                  </Col>
                </Row>
              </LegacyForm.Item>
              <Divider className={"tw:m-0!"} />
              <LegacyForm.Item noStyle>
                <Row gutter={16}>
                  <Col>
                    <Typography.Text type={"secondary"}>
                      {t("settlement.create.remainingAmountSettlement")}
                    </Typography.Text>
                  </Col>
                  <Col flex={1} className={"tw:flex tw:justify-end"}>
                    <Typography.Text>{overviewData?.balance}</Typography.Text>
                  </Col>
                </Row>
              </LegacyForm.Item>
            </>
          ) : null}
          <Col xs={24}>
            <AnchorButtons
              formStepOne={formStepOne}
              formStepTwo={formStepTwo}
              formStepThree={formStepThree}
              setOpenModalCancel={setOpenModalCancel}
              loadingStepOne={loadingStepOne}
              loadingStepTwo={loadingStepTwo}
              prev={prev}
              loadingDelete={loadingDelete}
              overviewData={overviewData}
            />
          </Col>
        </Space>
      </LegacyCard.Ant>
    </Affix>
  ) : (
    <Drawer
      title={
        <LegacyForm.Item noStyle>
          <div className={"tw:align-center tw:flex tw:justify-between"}>
            <Typography.Text type={"secondary"}>{t("settlement.create.settlementAmount")}</Typography.Text>
            <Flex>
              <Typography.Title
                className={"tw:m-0!"}
                level={5}
              >{`${formatLocalizedNumber({ value: Number(getAmount), options: { maximumFractionDigits: 2 } })} از ${formatLocalizedNumber(
                {
                  value: overview?.withdrawableBalance ?? 0,
                  options: { maximumFractionDigits: 2 },
                },
              )}`}</Typography.Title>
              <CuToman width={24} height={24} />
            </Flex>
          </div>
        </LegacyForm.Item>
      }
      closable={false}
      mask={false}
      open={true}
      placement={"bottom"}
      size={stepNumber === CreateStepEnum.IBANList ? 220 : 260}
    >
      <Space orientation={"vertical"} className={"tw:w-full"} size={"middle"}>
        {stepNumber !== CreateStepEnum.IBANList ? (
          <LegacyForm.Item noStyle>
            <div className={"tw:flex tw:justify-between"}>
              <div className={"tw:me-2! tw:shrink-0"}>
                <Typography.Text type={"secondary"}>{t("settlement.create.nameList")}</Typography.Text>
              </div>

              <Typography.Text ellipsis>
                <Tooltip title={requestTitle}>{requestTitle}</Tooltip>
              </Typography.Text>
            </div>
          </LegacyForm.Item>
        ) : null}
        {stepNumber !== CreateStepEnum.IBANList ? (
          <LegacyForm.Item noStyle>
            <div className={"tw:flex tw:justify-between"}>
              <div className={"tw:me-2! tw:shrink-0"}>
                <Typography.Text type={"secondary"}>{t("description")}</Typography.Text>
              </div>

              <Typography.Text ellipsis>
                <Tooltip title={requestDesc}>{requestDesc}</Tooltip>
              </Typography.Text>
            </div>
          </LegacyForm.Item>
        ) : null}
        {stepNumber !== CreateStepEnum.IBANList ? (
          <LegacyForm.Item noStyle>
            <Row gutter={16}>
              <Col>
                <Typography.Text type={"secondary"}>{t("settlement.create.settlementType")}</Typography.Text>
              </Col>
              <Col flex={1} className={"tw:flex tw:justify-end"}>
                <Typography.Text>{getLabelTransfer(transferType)}</Typography.Text>
              </Col>
            </Row>
          </LegacyForm.Item>
        ) : null}
        {stepNumber === CreateStepEnum.IBANList ? (
          <>
            {stepNumber !== CreateStepEnum.IBANList ? <Divider className={"tw:m-0!"} /> : null}
            <LegacyForm.Item noStyle>
              <Col xs={24} className={"tw:flex tw:justify-between"}>
                <Typography.Text type={"secondary"}>{t("settlement.create.settlementNumbers")}</Typography.Text>
                <Typography.Text>
                  {getSettlementListStepTwo?.length
                    ? t("settlement.create.showLength", { length: getSettlementListStepTwo.length })
                    : t("settlement.create.itemNotAdded")}
                </Typography.Text>
              </Col>
            </LegacyForm.Item>
            <LegacyForm.Item noStyle>
              <Col xs={24} className={"tw:flex tw:justify-between"}>
                <Typography.Text type={"secondary"}>{t("settlement.create.listStatus")}</Typography.Text>
                {getSettlementListStepTwo?.length ? (
                  <Typography.Text>
                    {errorCount ? (
                      <Space>
                        <CloseCircleFilled width={14} className={"color--danger tw:align-center tw:flex"} />
                        {t("settlement.create.showError", { error: errorCount })}
                      </Space>
                    ) : (
                      <Space>
                        <CheckCircleFilled width={14} className={"color--success tw:align-center tw:flex"} />
                        {t("settlement.create.noneError")}
                      </Space>
                    )}
                  </Typography.Text>
                ) : (
                  "-"
                )}
              </Col>
            </LegacyForm.Item>
          </>
        ) : null}
        <Col xs={24}>
          <AnchorButtons
            formStepOne={formStepOne}
            formStepTwo={formStepTwo}
            formStepThree={formStepThree}
            setOpenModalCancel={setOpenModalCancel}
            loadingStepOne={loadingStepOne}
            loadingStepTwo={loadingStepTwo}
            prev={prev}
            loadingDelete={loadingDelete}
            overviewData={overviewData}
          />
        </Col>
      </Space>
    </Drawer>
  );
};

export default DataCardStep;
